import {
  Check as CheckIcon,
  Security as SecurityIcon,
  TouchApp as TouchAppIcon,
  Settings as SettingsIcon,
  Payments as PaymentsIcon,
  VerifiedUser as VerifiedUserIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Key as KeyIcon,
  Link as LinkIcon,
  Webhook as WebhookIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Tabs,
  Tab,
  Divider,
  Alert,
  Box,
  Chip,
  Slider,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  Modal,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";

import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

// Add Satispay to our payment gateways
const paymentGateways = [
  {
    id: "stripe",
    name: "Stripe",
    description:
      "Affermato per l’elaborazione di pagamenti online con API avanzate e strumenti per sviluppatori.",
    setupTime: 60,
    monthlyFee: 0,
    transactionFee: "2.9% + $0.30",
    logo: "https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg",
    features: [
      "Credit/debit cards",
      "ACH",
      "Digital wallets",
      "International support",
      "Subscription billing",
    ],
    complexity: 35,
    configFields: [
      { id: "secret_key", label: "Secret Key", type: "text", required: true },
      { id: "activity_id", label: "Activity ID", type: "text", required: true },
      {
        id: "webhook_url",
        label: "Webhook URL",
        type: "text",
        defaultValue: "https://yourdomain.com/webhooks/stripe_webhook.php",
        readOnly: true,
      },
    ],
    webhookEvents: ["checkout.session.completed"],
  },
  {
    id: "paypal",
    name: "PayPal",
    description:
      "Piattaforma di pagamento ampiamente riconosciuta con opzioni di integrazione semplificate.",
    setupTime: 30,
    monthlyFee: 0,
    transactionFee: "2.9% + $0.30",
    logo: "https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg",
    features: [
      "Express checkout",
      "Buyer protection",
      "International payments",
      "Invoicing",
      "Mobile payments",
    ],
    complexity: 25,
    configFields: [
      { id: "client_id", label: "Client ID", type: "text", required: true },
      { id: "client_secret", label: "Client Secret", type: "text", required: true },
      { id: "activity_id", label: "Activity ID", type: "text", required: true },
      { id: "sandbox", label: "Sandbox Mode", type: "switch", defaultValue: true },
      {
        id: "webhook_url",
        label: "Webhook URL",
        type: "text",
        defaultValue: "https://yourdomain.com/webhooks/paypal_webhook.php",
        readOnly: true,
      },
    ],
    webhookEvents: ["CHECKOUT.ORDER.APPROVED"],
  },
  {
    id: "satispay",
    name: "Satispay",
    description: "Sistema di pagamento mobile italiano con integrazione semplice.",
    setupTime: 45,
    monthlyFee: 0,
    transactionFee: "0.5% + €0.20",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Logo_di_Satispay.svg/128px-Logo_di_Satispay.svg.png",
    features: [
      "Mobile payments",
      "QR code payments",
      "No credit card required",
      "Instant notifications",
      "Easy refunds",
    ],
    complexity: 30,
    configFields: [
      { id: "key_id", label: "Key ID", type: "text", required: true },
      {
        id: "private_key",
        label: "Private Key",
        type: "textarea",
        required: true,
        placeholder: "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----",
      },
      { id: "activity_id", label: "Activity ID", type: "text", required: true },
      { id: "sandbox", label: "Sandbox Mode", type: "switch", defaultValue: true },
      {
        id: "webhook_url",
        label: "Webhook URL",
        type: "text",
        defaultValue: "https://yourdomain.com/webhooks/satispay_webhook.php",
        readOnly: true,
      },
    ],
    webhookEvents: ["payment_authorized"],
  },
];

function PaymentGuidePage() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [complexity, setComplexity] = useState(30);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [currentGateway, setCurrentGateway] = useState(null);
  const [formData, setFormData] = useState({});

  const handleComplexityChange = (event, newValue) => {
    setComplexity(newValue);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenConfigModal = (gateway) => {
    setCurrentGateway(gateway);

    // Initialize form data with default values
    const initialData = {};
    gateway.configFields.forEach((field) => {
      if (field.defaultValue !== undefined) {
        initialData[field.id] = field.defaultValue;
      } else {
        initialData[field.id] = field.type === "switch" ? false : "";
      }
    });

    setFormData(initialData);
    setConfigModalOpen(true);
  };

  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
    setCurrentGateway(null);
  };

  const handleInputChange = (fieldId, value) => {
    setFormData({
      ...formData,
      [fieldId]: value,
    });
  };

  const handleSubmitConfig = () => {
    console.log("Submitting configuration for", currentGateway.name, formData);
    // Here you would typically send this data to your backend
    alert("Configuration saved successfully!");
    handleCloseConfigModal();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const marks = [
    { value: 0, label: "Beginner" },
    { value: 50, label: "Intermediate" },
    { value: 100, label: "Advanced" },
  ];

  // Filter payment gateways based on complexity
  const filteredGateways = paymentGateways.filter(
    (gateway) => Math.abs(gateway.complexity - complexity) < 25
  );

  return (
    <DashboardLayout>
      <ArgonBox mt={4} mb={5}>
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <ArgonBox>
            <ArgonTypography variant="h4" fontWeight="bold">
              Guida all integrazione dei Gateway di Pagamento
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text">
              Questa guida ti aiuta a configurare i 3 gateway supportati: Stripe, PayPal e Satispay
            </ArgonTypography>
          </ArgonBox>
          <Chip
            icon={<VerifiedUserIcon />}
            label="PCI Compliant"
            color="success"
            variant="outlined"
          />
        </ArgonBox>

        {/* Introduction card with slider */}
        <Card sx={{ mb: 4, overflow: "visible" }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <ArgonTypography variant="h5" fontWeight="bold" mb={1}>
                  Trova la soluzione di pagamento adatta alle tue esigenze
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text" mb={3}>
                  Seleziona il tuo livello di competenza tecnica per vedere i gateway di pagamento
                  raccomandati:
                </ArgonTypography>

                <Box sx={{ px: 3, my: 4 }}>
                  <Slider
                    value={complexity}
                    onChange={handleComplexityChange}
                    aria-labelledby="complexity-slider"
                    valueLabelDisplay="auto"
                    step={10}
                    marks={marks}
                    min={0}
                    max={100}
                    sx={{
                      color: theme.palette.primary.main,
                      "& .MuiSlider-thumb": {
                        height: 24,
                        width: 24,
                        "&:hover, &.Mui-focusVisible": {
                          boxShadow: `0px 0px 0px 8px ${alpha(theme.palette.primary.main, 0.16)}`,
                        },
                      },
                    }}
                  />
                </Box>

                <ArgonTypography variant="body2" color="text" mb={2}>
                  Le nostre raccomandazioni si basano sulla tua competenza tecnica, complessità di
                  integrazione e requisiti.
                </ArgonTypography>

                <Alert severity="info" sx={{ mt: 2 }}>
                  Tutti i gateway di pagamento raccomandati sono conformi a PCI-DSS e sicuri per
                  gestire i dati di pagamento.
                </Alert>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    p: 3,
                    borderRadius: 2,
                    height: "100%",
                  }}
                >
                  <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                    Vantaggi dei metodi di pagamento integrati
                  </ArgonTypography>

                  <Grid container spacing={2}>
                    {[
                      { icon: <SecurityIcon />, text: "Sicurezza avanzata con tokenizzazione" },
                      { icon: <TouchAppIcon />, text: "Migliore esperienza utente" },
                      { icon: <SettingsIcon />, text: "Esperienze di checkout personalizzabili" },
                      { icon: <PaymentsIcon />, text: "Supporto per più opzioni di pagamento" },
                    ].map((item, index) => (
                      <Grid item xs={6} key={index}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Avatar
                            sx={{
                              bgcolor: theme.palette.primary.main,
                              width: 30,
                              height: 30,
                              mr: 1,
                            }}
                          >
                            {item.icon}
                          </Avatar>
                          <ArgonTypography variant="body2">{item.text}</ArgonTypography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Recommended payment gateways */}
        <ArgonTypography variant="h5" fontWeight="bold" mb={3}>
          Gateway di Pagamento Raccomandati
        </ArgonTypography>

        <Grid container spacing={3} mb={4}>
          {filteredGateways.length > 0 ? (
            filteredGateways.map((gateway) => (
              <Grid item xs={12} md={4} key={gateway.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        component="img"
                        src={gateway.logo}
                        alt={gateway.name}
                        sx={{ height: 40, maxWidth: 120, mr: 2 }}
                      />
                      <ArgonTypography variant="h6" fontWeight="bold">
                        {gateway.name}
                      </ArgonTypography>
                    </Box>

                    <ArgonTypography variant="body2" color="text" mb={2}>
                      {gateway.description}
                    </ArgonTypography>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Tempo di setup
                        </ArgonTypography>
                        <ArgonTypography variant="body2">
                          {gateway.setupTime} minuti
                        </ArgonTypography>
                      </Grid>
                      <Grid item xs={6}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Commissione
                        </ArgonTypography>
                        <ArgonTypography variant="body2">{gateway.transactionFee}</ArgonTypography>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3 }}>
                      <ArgonTypography variant="caption" fontWeight="bold" color="text">
                        Funzionalità
                      </ArgonTypography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                        {gateway.features.map((feature, i) => (
                          <Chip key={i} label={feature} size="small" />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0, mt: "auto", display: "flex", gap: 2 }}>
                    <ArgonButton
                      color="primary"
                      variant="outlined"
                      onClick={() => {
                        setActiveTab(paymentGateways.findIndex((g) => g.id === gateway.id));
                        document
                          .getElementById("implementation-section")
                          .scrollIntoView({ behavior: "smooth" });
                      }}
                      sx={{ flexGrow: 1 }}
                    >
                      Guida Installazione
                    </ArgonButton>
                    <ArgonButton
                      color="primary"
                      variant="contained"
                      onClick={() => handleOpenConfigModal(gateway)}
                      sx={{ flexGrow: 1 }}
                    >
                      Configura
                    </ArgonButton>
                  </Box>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                Nessun gateway di pagamento corrisponde alle tue impostazioni attuali. Prova a
                regolare il cursore per vedere più opzioni.
              </Alert>
            </Grid>
          )}
        </Grid>

        {/* Integration guide specific to each gateway */}
        <div id="implementation-section">
          <ArgonTypography variant="h5" fontWeight="bold" mb={3}>
            Dettagli di Integrazione
          </ArgonTypography>

          <Card>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
              >
                {paymentGateways.map((gateway) => (
                  <Tab
                    key={gateway.id}
                    label={gateway.name}
                    icon={<img src={gateway.logo} alt={gateway.name} style={{ height: 20 }} />}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
            </Box>

            {/* Stripe Tab */}
            <Box
              role="tabpanel"
              hidden={activeTab !== 0}
              id="tabpanel-0"
              key="stripe"
              sx={{ p: 3 }}
            >
              {activeTab === 0 && (
                <GuideContent
                  gatewayId="stripe"
                  title="Integrazione Stripe"
                  steps={[
                    "Registrati su Stripe Dashboard",
                    "Recupera la tua chiave Secret Key da Developers > API keys",
                    "Inserisci i dati nella tabella stripe_configs con activity_id e secret_key",
                    "Imposta il webhook con gli eventi richiesti: checkout.session.completed",
                  ]}
                  webhookUrl="https://TUOSITO.COM/webhooks/stripe_webhook.php"
                  webhookEvents={["checkout.session.completed"]}
                  onOpenConfig={() => handleOpenConfigModal(paymentGateways[0])}
                />
              )}
            </Box>

            {/* PayPal Tab */}
            <Box
              role="tabpanel"
              hidden={activeTab !== 1}
              id="tabpanel-1"
              key="paypal"
              sx={{ p: 3 }}
            >
              {activeTab === 1 && (
                <GuideContent
                  gatewayId="paypal"
                  title="Integrazione PayPal"
                  steps={[
                    "Vai su PayPal Developer e crea un'app",
                    "Recupera Client ID e Client Secret dall'app",
                    "Inserisci in tabella paypal_configs con activity_id, client_id, client_secret, sandbox (1|0)",
                    "Imposta il webhook con gli eventi richiesti: CHECKOUT.ORDER.APPROVED",
                  ]}
                  webhookUrl="https://TUOSITO.COM/webhooks/paypal_webhook.php"
                  webhookEvents={["CHECKOUT.ORDER.APPROVED"]}
                  onOpenConfig={() => handleOpenConfigModal(paymentGateways[1])}
                />
              )}
            </Box>

            {/* Satispay Tab */}
            <Box
              role="tabpanel"
              hidden={activeTab !== 2}
              id="tabpanel-2"
              key="satispay"
              sx={{ p: 3 }}
            >
              {activeTab === 2 && (
                <GuideContent
                  gatewayId="satispay"
                  title="Integrazione Satispay"
                  steps={[
                    "Accedi al Satispay Business",
                    "Richiedi una chiave API con Key ID e Private Key (file PEM da importare come stringa)",
                    "Inserisci in satispay_configs: activity_id, key_id, private_key, sandbox",
                    "Imposta il webhook con gli eventi richiesti: payment_authorized",
                  ]}
                  webhookUrl="https://TUOSITO.COM/webhooks/satispay_webhook.php"
                  webhookEvents={["payment_authorized"]}
                  onOpenConfig={() => handleOpenConfigModal(paymentGateways[2])}
                />
              )}
            </Box>
          </Card>
        </div>
      </ArgonBox>

      {/* Configuration Modal */}
      <Dialog open={configModalOpen} onClose={handleCloseConfigModal} fullWidth maxWidth="md">
        {currentGateway && (
          <>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    component="img"
                    src={currentGateway.logo}
                    alt={currentGateway.name}
                    sx={{ height: 30, mr: 2 }}
                  />
                  <Typography variant="h6">Configura {currentGateway.name}</Typography>
                </Box>
                <IconButton onClick={handleCloseConfigModal}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <Alert severity="info" sx={{ mb: 3 }}>
                Inserisci i dati di configurazione per attivare {currentGateway.name} sulla tua
                piattaforma. Questi dati saranno salvati in modo sicuro nel database.
              </Alert>

              <Box sx={{ mb: 4 }}>
                <Stepper activeStep={0} orientation="horizontal">
                  <Step>
                    <StepLabel>Inserisci credenziali API</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Configura Webhook</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Test e attivazione</StepLabel>
                  </Step>
                </Stepper>
              </Box>

              <Grid container spacing={3}>
                {currentGateway.configFields.map((field) => (
                  <Grid
                    item
                    xs={12}
                    md={field.id === "private_key" || field.id === "webhook_url" ? 12 : 6}
                    key={field.id}
                  >
                    {field.type === "switch" ? (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData[field.id] || false}
                            onChange={(e) => handleInputChange(field.id, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={field.label}
                      />
                    ) : field.type === "textarea" ? (
                      <TextField
                        label={field.label}
                        multiline
                        rows={4}
                        fullWidth
                        value={formData[field.id] || ""}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        required={field.required}
                        placeholder={field.placeholder}
                        InputProps={{
                          startAdornment: field.id.includes("key") ? (
                            <KeyIcon position="start" sx={{ mr: 1, color: "action.active" }} />
                          ) : undefined,
                          readOnly: field.readOnly,
                        }}
                      />
                    ) : (
                      <TextField
                        label={field.label}
                        fullWidth
                        value={formData[field.id] || ""}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        required={field.required}
                        InputProps={{
                          startAdornment:
                            field.id.includes("key") || field.id.includes("secret") ? (
                              <KeyIcon position="start" sx={{ mr: 1, color: "action.active" }} />
                            ) : field.id === "webhook_url" ? (
                              <LinkIcon position="start" sx={{ mr: 1, color: "action.active" }} />
                            ) : undefined,
                          readOnly: field.readOnly,
                          endAdornment: field.readOnly ? (
                            <IconButton
                              onClick={() =>
                                copyToClipboard(formData[field.id] || field.defaultValue)
                              }
                              size="small"
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          ) : undefined,
                        }}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>

              {/* Webhook Events Section */}
              <Box sx={{ mt: 4, mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2, display: "flex", alignItems: "center" }}
                >
                  <WebhookIcon sx={{ mr: 1 }} /> Eventi Webhook Richiesti
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Grid container spacing={2}>
                    {currentGateway.webhookEvents.map((event, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Chip
                          label={event}
                          color="primary"
                          variant="outlined"
                          icon={<CodeIcon />}
                          sx={{ width: "100%", justifyContent: "flex-start" }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>

              <Alert severity="warning" sx={{ mt: 3 }}>
                Assicurati che il tuo server sia accessibile da internet e che l URL del webhook sia
                raggiungibile per ricevere notifiche di pagamento in tempo reale.
              </Alert>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <ArgonButton onClick={handleCloseConfigModal} color="secondary">
                Annulla
              </ArgonButton>
              <ArgonButton onClick={handleSubmitConfig} color="primary" variant="contained">
                Salva Configurazione
              </ArgonButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

// Component for displaying gateway integration guide
function GuideContent({ gatewayId, title, steps, webhookUrl, webhookEvents, onOpenConfig }) {
  const theme = useTheme();

  const getGatewayLink = (id) => {
    switch (id) {
      case "stripe":
        return "https://dashboard.stripe.com";
      case "paypal":
        return "https://developer.paypal.com";
      case "satispay":
        return "https://dashboard.satispay.com/business";
      default:
        return "#";
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <ArgonTypography variant="h6" fontWeight="bold" mb={3}>
            {title}
          </ArgonTypography>

          <Paper sx={{ p: 3, mb: 3 }}>
            <ArgonTypography variant="subtitle1" fontWeight="bold" mb={2}>
              Passaggi di integrazione
            </ArgonTypography>

            <Box component="ol" sx={{ pl: 2, mb: 3 }}>
              {steps.map((step, i) => (
                <Box component="li" key={i} sx={{ mb: 2 }}>
                  <ArgonTypography variant="body2">{step}</ArgonTypography>
                </Box>
              ))}
            </Box>
          </Paper>

          <ArgonButton
            color="primary"
            variant="outlined"
            fullWidth
            href={getGatewayLink(gatewayId)}
            target="_blank"
            sx={{ mb: 2 }}
          >
            Visita il Dashboard {gatewayId.charAt(0).toUpperCase() + gatewayId.slice(1)}
          </ArgonButton>

          <ArgonButton color="primary" variant="contained" fullWidth onClick={onOpenConfig}>
            Configura {gatewayId.charAt(0).toUpperCase() + gatewayId.slice(1)}
          </ArgonButton>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PaymentGuidePage;

GuideContent.propTypes = {
  gatewayId: PropTypes.oneOf(["stripe", "paypal", "satispay"]).isRequired,
  title: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  webhookUrl: PropTypes.string.isRequired,
  webhookEvents: PropTypes.arrayOf(PropTypes.string).isRequired,
  onOpenConfig: PropTypes.func.isRequired,
};

// If you have any default props, you can add them like this:
GuideContent.defaultProps = {
  webhookEvents: [],
};
